import type { GetMonthlyRevenueRequestParams } from '@musinsa/apis-curator';
import { getMonthlyRevenue } from '@musinsa/apis-curator';
import { lazyPipe } from '@musinsa/packages-functional';
import {
  validateServiceResponse,
  withServiceErrorCapture,
} from '@musinsa/services-common-service-zod4';

import {
  type GetMonthlyRevenueServiceResponse,
  GetMonthlyRevenueServiceResponseSchema,
} from './revenue.dto';

/**
 * 숫자를 원화 형식으로 포맷팅
 */
const formatCurrency = (amount: number): string => `${amount.toLocaleString('ko-KR')}원`;

/**
 * 날짜 범위를 포맷팅 (YYYY.MM.DD~YYYY.MM.DD)
 */
const formatDateRange = (firstDay: string, lastDay: string): string => {
  const formatDate = (dateStr: string) => {
    const date = new Date(dateStr);
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}.${month}.${day}`;
  };

  return `${formatDate(firstDay)}~${formatDate(lastDay)}`;
};

/**
 * API 응답을 Service DTO로 변환
 */
const transformMonthlyRevenueResponse = async (
  params: GetMonthlyRevenueRequestParams,
): Promise<GetMonthlyRevenueServiceResponse> => {
  const { data } = await getMonthlyRevenue(params);

  return {
    period: {
      dateRange: formatDateRange(data.period.firstDay, data.period.lastDay),
      isConfirmed: data.period.isConfirmed,
    },
    totalStats: {
      orderCount: data.totalOrderCount,
      orderAmount: formatCurrency(data.totalOrderAmount),
      earnings: formatCurrency(data.totalEarnings),
      visitCount: data.totalVisitCount,
    },
    goods: {
      earnings: {
        total: formatCurrency(data.goods.earnings.totalEarnings),
      },
      orderAmount: {
        total: formatCurrency(data.goods.orderAmount.totalAmount),
        direct: formatCurrency(data.goods.orderAmount.directAmount),
        indirect: formatCurrency(data.goods.orderAmount.indirectAmount),
        exclusive: formatCurrency(data.goods.orderAmount.exclusiveAmount),
      },
      orderCount: {
        total: data.goods.orderCount.totalCount,
        direct: data.goods.orderCount.directCount,
        indirect: data.goods.orderCount.indirectCount,
        exclusive: data.goods.orderCount.exclusiveCount,
      },
      visitCount: data.goods.visitCount,
    },
    curatorShop: {
      earnings: {
        total: formatCurrency(data.curatorShop.earnings.totalEarnings),
      },
      orderAmount: {
        total: formatCurrency(data.curatorShop.orderAmount.totalAmount),
        direct: formatCurrency(data.curatorShop.orderAmount.directAmount),
        indirect: formatCurrency(data.curatorShop.orderAmount.indirectAmount),
        exclusive: formatCurrency(data.curatorShop.orderAmount.exclusiveAmount),
      },
      orderCount: {
        total: data.curatorShop.orderCount.totalCount,
        direct: data.curatorShop.orderCount.directCount,
        indirect: data.curatorShop.orderCount.indirectCount,
        exclusive: data.curatorShop.orderCount.exclusiveCount,
      },
      visitCount: data.curatorShop.visitCount,
    },
    boostingPerformance: data.boostingPerformance
      ? {
          promotionName: data.boostingPerformance.promotionName,
          promotionPeriod: data.boostingPerformance.promotionPeriod,
          additionalCommissionRate: `${data.boostingPerformance.additionalCommissionRate}%`,
          expectedBoostingEarnings: formatCurrency(
            data.boostingPerformance.expectedBoostingEarnings,
          ),
        }
      : null,
  };
};

/**
 * 월별 수익 현황 조회
 */
export const getMonthlyRevenueService = withServiceErrorCapture(
  lazyPipe(
    transformMonthlyRevenueResponse,
    validateServiceResponse(GetMonthlyRevenueServiceResponseSchema),
  ),
);
